import React, { FC, useEffect, useRef } from 'react'
import { LogLeadTemp } from '../../../../interfaces/LogLeadTemp'
import { useIntl } from 'react-intl'
import { useState } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { Column, createColumnHelper, flexRender, useReactTable } from '@tanstack/react-table'
import { getCoreRowModel } from '@tanstack/react-table'
import { PaymentTermin, TempPaymentTermin } from '../../../../interfaces/Termin/Termin'
import { Switch } from "@mui/material";
import { usePipelineStore } from '../../../../stores/PipelineStore'
import { currencyFormatter, customNumberFormatString, getCurrencySymbol, getPinningStyles, renderWithFlexRender, renderWithouFlexRender } from '../../../../functions/general'
import moment from 'moment-timezone'
import { CustomField } from '../../../../interfaces/Settings'
import { useMerchantStore } from '../../../../stores/MerchantStore'
import { useMeetingStore } from '../../../../stores/MeetingsStore'
import { useSettingsStore } from '../../../../stores/SettingsStore'
import { SimplePipeline } from '../../../../interfaces/Pipeline'
import { SimpleMerchant } from '../../../../interfaces/Merchant'
import { SimpleMeeting } from '../../../../interfaces/Meeting'
import useModalEffect from '../../../../hooks/useModalEffect'
import TerminAttachment from '../../../../components/Pipelines/DetailTermin/TerminAttachment'
import PreviewAttachmentTermin from '../../../../components/Pipelines/DetailTermin/PreviewAttachmentTermin'

interface DetailTerminLogTempProps {
    logLeadTemp: LogLeadTemp
}

const DetailTerminLogTemp: FC<DetailTerminLogTempProps> = ({ logLeadTemp }) => {
    const intl = useIntl()
    const { settings } = useSettingsStore()
    const columnHelper = createColumnHelper<PaymentTermin>()
    const { selectMultiPipeline } = usePipelineStore()
    const [customFieldTermin, setCustomFieldTermin] = useState<CustomField>({})
    const [showAttachmentModal, setShowAttachmentModal] = useState(false)
    const [terminKey, setTerminKey] = useState<string>('')
    const [terminUrl, setTerminUrl] = useState<string>('')
    const [terminFiles, setTerminFiles] = useState<File | File[] | null>(null)
    const [isTypeFile, setIsTypeFile] = useState<boolean>(false)
    const { simplePipelines } = usePipelineStore()
    const { simpleMerchants } = useMerchantStore()
    const { simpleMeetings } = useMeetingStore()
    const modalRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (settings.termins_custom_fields) {
            setCustomFieldTermin(JSON.parse(settings.termins_custom_fields))
        }
    }, [settings])

    const handleCloseModal = () => {
        if (modalRef.current) {
            modalRef.current.click()
        }
    }

    const handleClosePreview = () => {
        setShowAttachmentModal(false);
        setTerminFiles([]);
    }

    const handleCheckIsTypeAttachment = (url: string | string[] | File | File[], key?: string) => {
        const isFileArray = (value: any[]): value is File[] => {
            return value.every((item): item is File => item instanceof File);
        };

        const isFile = url instanceof File || (Array.isArray(url) && isFileArray(url));

        setIsTypeFile(isFile);

        if (isFile) {
            setTerminFiles(url as File | File[]);
            setShowAttachmentModal(true)
        } else {
            setTerminKey(key || '');
            setTerminUrl(url as any);
            setShowAttachmentModal(true)
        }
    };

    const columnCustomField = Object.keys(customFieldTermin).map(key => {
        const fieldType = customFieldTermin[key].type

        if (fieldType === 'text' || fieldType === 'option' || fieldType === 'multiple' || fieldType === 'multipleOption') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 25,
                cell: (info) => {
                    const row = info.row.original;
                    const values = row.customFields && row.customFields[key] && row.customFields[key].toString();
                    return values ? values.split(',').join(', ') : '-';
                },
            })
        } else if (fieldType === 'date') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 30,
                cell: (info) => {
                    const value = info.getValue()
                    return value ? moment(value).format("DD MMM YYYY") : '-'
                },
            })
        } else if (fieldType === 'dateTimeRange') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 30,
                cell: (info) => {
                    const value = info.getValue()
                    const convertValues = value?.map((item: any) => moment(item).format("DD MMM YYYY HH:mm"))
                    return convertValues ? convertValues.join(' - ') : '-'
                },
            })
        } else if (fieldType === 'image' || fieldType === 'images') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 25,
                cell: (info) => {
                    const row = info.row.original;
                    const imageUrl = row.customFields && row.customFields[key];
                    return imageUrl ? (
                        <span role="button" onClick={() => {
                            handleCheckIsTypeAttachment(imageUrl, key)
                        }}>
                            <i className="fas fs-2 fa-eye"></i>
                        </span>
                    ) : '-';
                },
            })
        } else if (fieldType === 'file' || fieldType === 'files') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 25,
                cell: (info) => {
                    const row = info.row.original;
                    const fileUrl = row.customFields && row.customFields[key];
                    if (typeof fileUrl === "string") {
                        return fileUrl ? (
                            <a
                                href={fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="ms-2 btn btn-sm btn-icon btn-bg-light btn-active-color-primary"
                            >
                                <i className="fas fa-file-invoice fs-3"></i>
                            </a>
                        ) : '-';
                    } else {
                        return fileUrl ? (
                            <span role="button" onClick={() => {
                                handleCheckIsTypeAttachment(fileUrl, key)
                            }}>
                                <i className="fas fa-file-invoice fs-3"></i>
                            </span>
                        ) : '-';
                    }
                },
            })
        } else if (fieldType === 'serial') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 25,
                cell: (info) => {
                    const row = info.row.original;
                    const values = row.customFields && row.customFields[key];
                    return typeof values === 'string' ? values : (values as string[])?.length > 0 ? (values as string[]).join('') : '-';
                },
            })
        } else if (fieldType === 'link') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 25,
                cell: (info) => {
                    const row = info.row.original;
                    const values = row.customFields && row.customFields[key];
                    return values ? (
                        <a href={values} target="_blank" rel="noreferrer"
                            className="btn btn-sm btn-outline btn-outline-default btn-active-light text-gray-600">
                            <i className="fas fa-external-link-alt fs-6 ms-2 p-0 me-2"></i>
                            {intl.formatMessage({ id: 'FORM.LABEL.LOOK' })}
                        </a>
                    ) : '-';
                },
            })
        } else if (fieldType === 'phone') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 30,
                cell: (info) => {
                    const row = info.row.original;
                    const values = row.customFields && row.customFields[key];
                    return values ? values : '-';
                },
            })
        } else if (fieldType === 'number') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 30,
                cell: (info) => {
                    return info.getValue() ? customNumberFormatString(info.getValue().toString()) : '-';
                },
            })
        } else if (fieldType === 'time') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 30,
                cell: (info) => {
                    return info.getValue() ? moment(info.getValue()).format("HH:mm") : '-';
                },
            })
        } else if (fieldType === 'pipeline') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 30,
                cell: (info) => {
                    const row = info.row.original;
                    const values = row.customFields && row.customFields[key];
                    const findData = simplePipelines.find((item: SimplePipeline) => item.id === values);
                    if (findData) {
                        return (
                            <div className="d-flex flex-column">
                                <span><b>{findData.name}</b> / </span>
                                <span>{currencyFormatter(findData.nominal, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode as any, { showPrefix: true })} / </span>
                                <span>{moment(findData.dateCreated).format("DD MMM YYYY")}</span>
                            </div>
                        );
                    }
                    return '-';
                },
            })
        } else if (fieldType === 'merchant') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 30,
                cell: (info) => {
                    const row = info.row.original;
                    const values = row.customFields && row.customFields[key];
                    const findData = simpleMerchants.filter((item: SimpleMerchant) => (values ?? []).map((item: any) => item).includes(item.id));
                    if (findData && findData.length > 0) {
                        return findData.map((item: SimpleMerchant, index: number) => (
                            <div className="d-flex flex-column">
                                <span><b>{item.name}</b> / </span>
                                <span>{item.phone} {index !== findData.length - 1 && ','}</span>
                            </div>
                        ));
                    }
                    return '-';
                },
            })
        } else if (fieldType === 'meeting') {
            return columnHelper.accessor(row => row.customFields?.[key], {
                id: key,
                header: () => customFieldTermin[key].name,
                size: 30,
                cell: (info) => {
                    const row = info.row.original;
                    const values = row.customFields && row.customFields[key];
                    const findData = simpleMeetings.filter((item: SimpleMeeting) => (values ?? []).map((item: any) => item).includes(item.id));
                    if (findData && findData.length > 0) {
                        return findData.map((item: SimpleMeeting, index: number) => (
                            <div className="d-flex flex-column">
                                <span><b>{item.name}</b> / </span>
                                <span>{moment(item.dateMeet).format('DD MMM YYYY')} {index !== findData.length - 1 && ','}</span>
                            </div>
                        ));
                    }
                    return '-';
                }
            })
        }
        return null;
    }).filter((column): column is NonNullable<typeof column> => column !== null);

    const columns = [
        columnHelper.accessor(row => row.id, {
            id: 'termNo',
            header: () => intl.formatMessage({ id: 'FORM.LABEL.TABLE.TERM_TO' }),
            size: 25,
            cell: (info) => (
                <div className="d-flex align-items-center gap-2">
                    <span>{info.row.index + 1}</span>
                    {info.row.index === 0 && info.row.original.isDownPayment && (
                        <span className="badge badge-primary">DP</span>
                    )}
                </div>
            ),
        }),
        columnHelper.accessor('nominalBeforeVat', {
            header: () => `Nominal (${selectMultiPipeline.currencyCode})`,
            size: 30,
            cell: (info) => {
                const row = info.row.original;
                const isCompanyCurrency = selectMultiPipeline.currencyCode
                const isCompanyCurrencyNeedRound = selectMultiPipeline.currencyIsRounded
                return (
                    <span className='d-flex justify-content-between align-items-center gap-3'>
                        <span>{getCurrencySymbol(isCompanyCurrency)}</span>
                        <span>{currencyFormatter((row.nominal).toString(), isCompanyCurrencyNeedRound, isCompanyCurrency)}</span>
                    </span>)
            },
        }),
        columnHelper.accessor('vatPercentage', {
            header: () => intl.formatMessage({ id: 'FORM.LABEL.TABLE.VAT' }),
            size: 30,
            cell: (info) => {
                const _vat = info.getValue()
                const vat = _vat ? customNumberFormatString(_vat.toString()) : undefined

                return (
                    <span className='d-flex justify-content-between align-items-center gap-3'>
                        <span>
                            {vat || "-"}
                        </span>
                        <span>{vat ? "%" : ""}</span>
                    </span>)
            },
        }),
        columnHelper.accessor('vatNominal', {
            header: () => `Total (${selectMultiPipeline.currencyCode})`,
            size: 30,
            cell: (info) => {
                const row = info.row.original;
                const isCompanyCurrency = selectMultiPipeline.currencyCode
                const isCompanyCurrencyNeedRound = selectMultiPipeline.currencyIsRounded
                return (
                    <span className='d-flex justify-content-between align-items-center gap-3'>
                        <span>{getCurrencySymbol(isCompanyCurrency)}</span>
                        <span>{currencyFormatter((row.total || 0).toString(), isCompanyCurrencyNeedRound, isCompanyCurrency)}</span>
                    </span>)
            },
        }),
        columnHelper.accessor('dateDue', {
            header: () => intl.formatMessage({ id: 'FORM.LABEL.DEADLINE_PAYMENT' }),
            size: 25,
            cell: (info) => {
                return moment(info.getValue()).format("DD MMM YYYY")
            },
        }),
        columnHelper.accessor('datePaid', {
            header: () => intl.formatMessage({ id: 'FORM.LABEL.PAYMENT_DATE' }),
            size: 25,
            cell: (info) => {
                const getValue = info.getValue();
                if (getValue) {
                    return moment(getValue).format("DD MMM YYYY")
                }
                return <span>-</span>
            },
        }),
        columnHelper.accessor('receiptUrls', {
            header: () => intl.formatMessage({ id: 'FORM.LABEL.PROOF_OF_PAYMENT_TERM' }),
            size: 30,
            cell: (info) => {
                const row = info.row.original;
                const imageUrl = row.receiptUrls
                const isExist = typeof imageUrl === 'object' ? (imageUrl as unknown as File).name ? true : false : true
                return isExist ? (
                    <span role="button" onClick={() => {
                        handleCheckIsTypeAttachment(imageUrl, 'receiptUrls')
                    }}>
                        {intl.formatMessage({ id: 'FORM.LABEL.LOOK' })}
                        <i className="fas fa-external-link-alt fs-6 ms-2 p-0"></i>
                    </span>
                ) : (
                    <span>{intl.formatMessage({ id: 'FORM.LABEL.NO_PROOF' })}</span>
                );
            },
        }),
        columnHelper.accessor('progress', {
            header: () => intl.formatMessage({ id: 'FORM.LABEL.STATUS_OF_PAYMENT' }),
            size: 25,
            cell: (info) => (
                info.getValue() === 'PENDING' ?
                    <span className='badge bg-warning'>{info.getValue()}</span> :
                    <span className='badge bg-success'>{info.getValue()}</span>
            ),
        }),
        ...columnCustomField,
    ]

    const table = useReactTable({
        data: logLeadTemp.termins,
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnPinning: {
                left: ['actions']
            }
        }
    })

    useModalEffect({
        showModal: showAttachmentModal,
        setShowModal: setShowAttachmentModal,
        modalId: `attachments-detail-termin-modal-${logLeadTemp.id}`
    });

    return (
        <>
            <div className="modal fade" tabIndex={-1} id={`detail-termin-log-temp-modal-${logLeadTemp.id}`} ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title w-100">{intl.formatMessage({ id: "FORM.MENU.TERM_DETAILS" })} {logLeadTemp.pipelineName}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <div className="modal-body">
                            <div className="table-responsive">
                                <table className="table table-row-dashed table-row-gray-230 align-middle gs-0 gy-4 text-center">
                                    <thead>
                                        <tr className="fw-bolder text-dark" style={{ backgroundColor: '#D4D4D4' }}>
                                            {table.getHeaderGroups().map(headerGroup => (
                                                headerGroup.headers.map((header, index) => (
                                                    <th
                                                        key={header.id}
                                                        style={{
                                                            padding: `12px ${header.column.columnDef.size}px`,
                                                            whiteSpace: 'nowrap',
                                                            minWidth: 'fit-content',
                                                            ...getPinningStyles(header.column as Column<PaymentTermin | TempPaymentTermin>, true)
                                                        }}
                                                        className="align-middle"
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                    </th>
                                                ))
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map(row => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map((cell, index) => (
                                                    <td
                                                        key={cell.id}
                                                        style={{
                                                            padding: `12px ${cell.column.columnDef.size}px`,
                                                            whiteSpace: 'nowrap',
                                                            minWidth: 'fit-content',
                                                            ...getPinningStyles(cell.column as Column<PaymentTermin | TempPaymentTermin>, false)
                                                        }}
                                                        className="align-middle"
                                                    >
                                                        {index === 0 ? renderWithouFlexRender(cell) : renderWithFlexRender(cell)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="col-12 text-end">
                                <button
                                    type="button"
                                    className="btn btn-light me-3"
                                    onClick={handleCloseModal}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div >

            {
                showAttachmentModal && terminUrl && isTypeFile === false && (
                    <TerminAttachment
                        pipeline={logLeadTemp}
                        title={customFieldTermin[terminKey]?.name ?? ''}
                        url={terminUrl}
                        type='log-lead-temp'
                    />
                )
            }

            {
                showAttachmentModal && terminFiles && isTypeFile === true && (
                    <PreviewAttachmentTermin
                        files={terminFiles!}
                        onClose={handleClosePreview}
                    />
                )
            }
        </>
    )
}

export default DetailTerminLogTemp